import { gql } from '@apollo/client';

/* ── clients ────────────────────────────────────────────────── */

export const GET_CLIENTS = gql`
  query GetClients($ids: [Int!]) {
    getAllUsers(ids: $ids) {
      total
      items {
        id
        name
        email
        phoneNumber
        userType
        isActive
        kycVerified
        kycPanMasked
        createdAt
        updatedAt
        organizationId
      }
    }
  }
`;

/**
 * One client's KYC, from the onboarding record that actually holds it.
 *
 * NOT `getAllUsers.kycVerified`. That flag is false on every user row on this
 * backend, and the identifier columns beside it — masked PAN, Aadhaar last 4,
 * CKYC number — are null on all of them too, so a screen reading it reports
 * "pending" for clients who are verified. The onboarding record is where the
 * status lives, and it is a state rather than a flag: IN_PROGRESS, PENDING,
 * VERIFIED, REJECTED or EXPIRED, per `getKycStatusEnum`.
 *
 * `currentStep` is how far the form got and moves independently of the status —
 * a client can be VERIFIED while still sitting on PERSONAL_DETAILS — so the
 * status is what a chip should show.
 *
 * `ucc` with `uccStatus: ACTIVE` is the exchange code that lets the client
 * actually transact. Verified without one is a real state and not the same
 * thing, which is why both are selected.
 *
 * One user per call: there is no bulk form, and no KYC status on the user list
 * response, so this is for a single-client screen only.
 */
export const KYC_ONBOARDING = gql`
  query KycOnboarding($userId: Int) {
    getKycOnboarding(userId: $userId) {
      success
      message
      kycStatus
      externalKycStatus
      currentStep
      ucc
      uccStatus
      accountId
      kycLinkSentAt
      data {
        personalDetails {
          fullName email mobileNumber dateOfBirth placeOfBirth gender
          fatherName motherName maritalStatus
          address city state pincode country
          annualIncome occupation tradingExperience riskProfile communicationMode
        }
        identityVerification { panNumber aadharNumber }
        bankDetails { accountNumber ifscCode branchName dividendMode }
        declarations {
          paysTaxesInIndia notPoliticallyExposed acceptIbtTerms
          fundSettlementDays noSebiActions
        }
        consents { acceptAllTerms bseStarMfFacility }
        nomineeDetails {
          totalNominees totalPercentage
          nominees {
            id sequenceNumber nomineeName nomineeRelationship nomineeDob
            nomineePercentage nomineeMobileNumber nomineeEmail
            nomineeAddress nomineeCity nomineeState nomineePincode nomineeCountry
            nomineeIdProofType nomineeIdProofNumber
            isMinor guardianName guardianRelationship
          }
        }
        documentSigning { selfieUrl esignatureImageUrl esignatureCompleted }
        submitted { submittedAt }
      }
    }
  }
`;

/**
 * The option lists the onboarding form offers, from the backend rather than
 * typed out here.
 *
 * Two shapes on one type, which is easy to get wrong: the first five are plain
 * [String!], while every other list is [CodeOption!] and needs its subfields
 * selected. Reading them from the server is the point — a hardcoded state or
 * income list drifts silently the moment the backend adds a member.
 */
export const KYC_ENUM_OPTIONS = gql`
  query KycEnumOptions {
    getKycOnboardingEnumOptions {
      incomeRanges
      tradingExperiences
      riskProfiles
      bankAccountTypes
      onboardingSteps
      states { value label code }
      countries { value label code }
      communicationModes { value label code }
      dividendModes { value label code }
      nomineeRelationships { value label code }
      nomineeIdentityTypes { value label code }
      guardianRelationships { value label code }
    }
  }
`;

export const GET_PROSPECTS = gql`
  query GetProspects($filter: ListProspectsFilterInput) {
    listProspects(filter: $filter) {
      total
      items { id name email phoneNumber status source createdAt }
    }
  }
`;
